import { Fragment, type MouseEvent, useCallback, useEffect, useRef, useState } from 'react';

import { Box, Col, Row } from '@opendoor/atoms/box';
import { Image } from '@opendoor/atoms/image';
import { Text } from '@opendoor/atoms/text';
import { cn } from '@opendoor/atoms/utils';
import { globalObservability } from '@opendoor/observability/slim';
import { IconHeartFilled } from '@tabler/icons-react';
import { buildPropertyPhotoUrl } from 'components/marketplace/property/helpers';
import { useMediaQuery } from 'components/shared/hooks/useMediaQuery';
import Router from 'next/router';
import { Marker, Popup } from 'react-map-gl/mapbox';

import type { MapMarkerProps } from './types';

import styles from './MapMarker.module.css';

function formatNumber(number: number) {
  return number.toLocaleString('en-US', {
    minimumFractionDigits: 0,
    maximumFractionDigits: number >= 1_000_000 ? 2 : 0,
    notation: 'compact',
    compactDisplay: 'short',
  });
}

const HOVER_INTENT_MS = 120;

// Dedupe RUM impressions so panning does not fire a new `mapMarkerHover` every
// time a popup opens. The Set is cleared on SPA route changes so navigating
// between markets (/homes/a -> /homes/b) re-fires impressions for repeated
// addresses that appear in the new market's results.
const markerImpressionsFired = new Set<string>();
let routerListenerAttached = false;

function ensureRouterListener() {
  if (routerListenerAttached || typeof window === 'undefined') {
    return;
  }
  routerListenerAttached = true;
  Router.events.on('routeChangeStart', () => {
    markerImpressionsFired.clear();
  });
}

// Test-only reset. The dedupe Set persists for the SPA session, so unit tests
// that render MapMarker multiple times need a way to reset it without relying
// on salted address tokens that mask bugs in the dedupe contract itself.
export const __resetMarkerImpressionsForTest = () => {
  markerImpressionsFired.clear();
};

function trackMarkerImpression(addressToken: string) {
  if (!addressToken || markerImpressionsFired.has(addressToken)) {
    return;
  }
  ensureRouterListener();
  markerImpressionsFired.add(addressToken);
  try {
    globalObservability.getDatadogRumClient?.()?.addAction?.('marketplace.mapMarkerHover', {
      addressToken,
    });
  } catch {
    // analytics must never break the UI
  }
}

function buildPhotoSlots(photos: readonly string[] | null | undefined) {
  const [primary, secondary, tertiary] = photos ?? [];
  return {
    primary: buildPropertyPhotoUrl(primary, { preset: '3:2-sm' }),
    secondary: buildPropertyPhotoUrl(secondary, { preset: '3:2-xs' }),
    tertiary: buildPropertyPhotoUrl(tertiary, { preset: '3:2-xs' }),
  };
}

function buildMarkerAriaLabel(property: MapMarkerProps['property']) {
  const parts: string[] = [];
  if (property.listPriceCents != null) {
    parts.push(`Property listed at $${(property.listPriceCents / 100).toLocaleString()}`);
  } else {
    parts.push('Property');
  }
  const beds = property.bedrooms != null ? `${property.bedrooms} beds` : null;
  const baths = property.bathrooms != null ? `${property.bathrooms} baths` : null;
  const summary = [beds, baths].filter(Boolean).join(', ');
  if (summary) {
    parts.push(summary);
  }
  if (property.isLiked) {
    parts.push('favorited');
  }
  return parts.join(', ');
}

type PopupImageProps = {
  uri: string | null;
  alt: string;
  className: string;
};

const PopupImage = ({ uri, alt, className }: PopupImageProps) => {
  const [errored, setErrored] = useState(false);

  if (!uri || errored) {
    return <Box className={cn('bg-od-bg-secondary', className)} />;
  }

  return (
    <Image
      alt={alt}
      src={uri}
      onError={() => setErrored(true)}
      className={cn('object-cover', className)}
    />
  );
};

type PopupBodyProps = {
  property: MapMarkerProps['property'];
};

// Not wrapped in `memo`: callers pass a fresh `property` object on every
// MapMarker render, so shallow-prop comparison would always miss. The popup
// body only mounts while it is open, so re-renders are cheap enough to skip
// the optimization rather than add a custom comparator.
const PopupBody = ({ property }: PopupBodyProps) => {
  const slots = buildPhotoSlots(property.photoUrls);

  return (
    <Col className={styles.popupEnter}>
      <PopupImage
        uri={slots.primary}
        alt="map-marker-primary-property-image"
        className="aspect-[7/3] w-88 rounded-t-2xl"
      />
      <Row className="justify-between pt-1.5">
        <PopupImage
          uri={slots.secondary}
          alt="map-marker-secondary-property-image"
          className="aspect-[43/25] w-43"
        />
        <PopupImage
          uri={slots.tertiary}
          alt="map-marker-tertiary-property-image"
          className="aspect-[43/25] w-43"
        />
      </Row>
      <Col className="gap-1.5 px-5 pb-4 pt-5">
        <Row className="items-baseline justify-between">
          {property.listPriceCents != null ? (
            <Text as="p" className="text-label-xl text-od-content-primary">
              {`$${(property.listPriceCents / 100).toLocaleString()}`}
            </Text>
          ) : null}
          {property.isLiked ? (
            <IconHeartFilled size={14} className="self-center text-od-content-error" />
          ) : null}
        </Row>
        {(() => {
          const stats = [
            property.bedrooms != null ? `${property.bedrooms} bds` : null,
            property.bathrooms != null ? `${property.bathrooms} ba` : null,
            property.totalLivingSquareFootage != null
              ? `${property.totalLivingSquareFootage.toLocaleString()} sqft`
              : null,
          ].filter((part): part is string => part !== null);
          if (stats.length === 0) {
            return null;
          }
          return (
            <Row className="items-baseline">
              {stats.map((part, index) => (
                <Fragment key={part}>
                  {index > 0 ? (
                    <Text as="span" className="text-body-sm text-od-content-inactive-secondary">
                      {' • '}
                    </Text>
                  ) : null}
                  <Text as="span" className="text-body-sm text-od-content-secondary">
                    {part}
                  </Text>
                </Fragment>
              ))}
            </Row>
          );
        })()}
        {property.street ? (
          <Text as="p" className="truncate text-body-sm text-od-content-tertiary">
            {property.street}
          </Text>
        ) : null}
      </Col>
    </Col>
  );
};

export const MapMarker = ({ property, onClick, isSelected, isLoading }: MapMarkerProps) => {
  const isDesktop = useMediaQuery('(min-width: 768px)');
  const [isHovering, setIsHovering] = useState(false);
  const [isFocused, setIsFocused] = useState(false);
  const isPopupOpen = isHovering || isFocused;
  const isHighlighted = isPopupOpen || Boolean(isSelected) || Boolean(isLoading);
  const hoverTimerRef = useRef<number | null>(null);
  // Keyed by the preloaded href so the effect re-fires when the primary photo
  // URL changes. A simple boolean guard would block all subsequent preloads.
  const preloadedHrefRef = useRef<string | null>(null);
  const popupId = `cosmos-map-marker-popup-${property.addressId}`;

  useEffect(() => {
    if (isPopupOpen) {
      trackMarkerImpression(property.addressId);
    }
  }, [isPopupOpen, property.addressId]);

  useEffect(() => {
    if (!isPopupOpen || !isDesktop) {
      return;
    }
    const primary = property.photoUrls[0];
    if (!primary) {
      return;
    }
    const href = buildPropertyPhotoUrl(primary, { preset: '3:2-sm' });
    if (!href) {
      return;
    }
    if (preloadedHrefRef.current === href) {
      return;
    }
    preloadedHrefRef.current = href;
    const link = document.createElement('link');
    link.rel = 'preload';
    link.as = 'image';
    link.href = href;
    link.fetchPriority = 'high';
    document.head.appendChild(link);
    return () => {
      link.parentNode?.removeChild(link);
    };
  }, [isDesktop, isPopupOpen, property.photoUrls]);

  useEffect(() => {
    if (!isFocused) {
      return;
    }
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        setIsFocused(false);
      }
    };
    window.addEventListener('keydown', handler);
    return () => {
      window.removeEventListener('keydown', handler);
    };
  }, [isFocused]);

  const cancelHoverTimer = useCallback(() => {
    if (hoverTimerRef.current != null) {
      window.clearTimeout(hoverTimerRef.current);
      hoverTimerRef.current = null;
    }
  }, []);

  useEffect(() => {
    return () => cancelHoverTimer();
  }, [cancelHoverTimer]);

  const handleOnHover = () => {
    cancelHoverTimer();
    hoverTimerRef.current = window.setTimeout(() => {
      setIsHovering(true);
    }, HOVER_INTENT_MS);
  };

  const handleOnMouseOut = () => {
    cancelHoverTimer();
    setIsHovering(false);
  };

  const handleOnFocus = () => {
    setIsFocused(true);
  };

  const handleOnBlur = () => {
    setIsFocused(false);
  };

  const handleOnClick = (event: MouseEvent) => {
    event.stopPropagation();
    cancelHoverTimer();
    setIsHovering(false);
    setIsFocused(false);
    onClick();
  };

  const ariaLabel = buildMarkerAriaLabel(property);

  return (
    <Marker latitude={property.latitude} longitude={property.longitude}>
      <Row
        aria-label={ariaLabel}
        aria-expanded={isPopupOpen && isDesktop}
        aria-describedby={isPopupOpen && isDesktop ? popupId : undefined}
        analyticsName="property-marker-click"
        tabIndex={0}
        onMouseEnter={handleOnHover}
        onMouseLeave={handleOnMouseOut}
        onFocus={handleOnFocus}
        onBlur={handleOnBlur}
        onClick={handleOnClick}
        className={cn(
          'z-[1] h-7 items-center justify-center rounded-full shadow-[0_2px_4px_rgba(0,0,0,0.18),0_0_0_1px_rgba(0,0,0,0.08)]',
          property.isLiked ? 'w-19' : 'w-15',
          isHighlighted ? 'bg-od-bg-inverse-primary' : 'bg-od-bg-primary',
        )}
      >
        {isLoading ? (
          <Text as="span" className="text-label-sm text-od-content-inverse-primary">
            •••
          </Text>
        ) : (
          <>
            {property.isLiked ? (
              <IconHeartFilled size={14} className="pr-1 text-od-content-error" />
            ) : null}
            {property.listPriceCents != null ? (
              <Text
                as="span"
                className={cn(
                  'text-label-sm',
                  isHighlighted ? 'text-od-content-inverse-primary' : 'text-od-content-primary',
                )}
              >
                {formatNumber(property.listPriceCents / 100)}
              </Text>
            ) : null}
          </>
        )}
      </Row>
      {isPopupOpen && isDesktop ? (
        <Popup
          longitude={property.longitude}
          latitude={property.latitude}
          anchor="bottom"
          offset={20}
          closeButton={false}
        >
          <Box id={popupId} role="region">
            <PopupBody property={property} />
          </Box>
        </Popup>
      ) : null}
    </Marker>
  );
};
